import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import TextoMarkdown from './ajuda/TextoMarkdown';
import { definirAulaAberta } from './ajuda/ajudaAulaStore';
import { MODO_SUPORTE_CHAT } from '../config/suporteChat';
import '../styles/DuvidaIA.css';

// "Tirar dúvida com IA" na tela de estudo do aluno (função de IA duvidas_aluno, backend
// routes/duvidasAluno.js). Botão flutuante que abre um painel ao lado da aula, sem sair da tela:
// conversa curta (só nesta tela; nada é gravado no servidor), indicador de carregamento e as
// perguntas que restam hoje.
//
//   <DuvidaIA turmaId={id} aulaId={id} aulaTitulo="..." />
//
// Some quando: a função está desligada (GET /duvidas-aluno/estado -> visivel false), o aluno está
// com uma prova aberta (estado.emProva, ou 403 em_prova numa pergunta) ou uma prova é aberta em
// outra aba (evento `storage` com CHAVE_PROVA_ABERTA, gravada pela tela de provas). O bloqueio
// durante a prova é garantido no servidor; aqui é só para não oferecer o que não vai funcionar.
//
// O painel não é modal: a aula continua visível e rolável ao lado (no celular ele ocupa a tela).
// Esc fecha e o foco volta ao botão.
//
// Com a bolha "Ajuda" ligada (REACT_APP_SUPORTE_CHAT=ia, config/suporteChat.js), este componente
// não desenha botão: só registra a aula aberta (ajuda/ajudaAulaStore) e a bolha oferece a
// "Dúvida sobre esta aula" ao lado da "Ajuda com a plataforma", abrindo DuvidaIAConversa no modo
// controlado (sem botão próprio; `aberto` e `aoFechar` vêm da bolha).

export const CHAVE_PROVA_ABERTA = 'exxer:prova-aberta';
const MOTIVOS_LIMITE = ['limite_usuario', 'limite_mensal', 'teto_atingido'];
const HISTORICO_ENVIADO = 6;

/** Resposta de erro da API -> chave de texto (studentExtra:aiDoubts.errors.*). */
export const chaveDoErro = (status, dados = {}) => {
  const { codigo, motivo } = dados || {};
  if (codigo === 'em_prova') return 'inExam';
  if (motivo === 'limite_mensal') return 'limitMonth';
  if (motivo === 'teto_atingido') return 'limitPlatform';
  if (codigo === 'limite' || MOTIVOS_LIMITE.includes(motivo)) return 'limitDay';
  if (motivo === 'funcao_desligada' || motivo === 'sem_chave') return 'disabled';
  if (codigo === 'bloqueado' || motivo === 'bloqueado_pelo_admin' || motivo === 'bloqueado') return 'blocked';
  if (codigo === 'politica') return 'policy';
  if (codigo === 'recusa') return 'refusal';
  if (codigo === 'aula_negada' || codigo === 'aula') return 'lessonLocked';
  if (status === 400) return 'invalid';
  return 'generic';
};

export const DuvidaIAConversa = ({
  turmaId, aulaId, aulaTitulo = '', controlado = false, aberto: abertoExterno = false, aoFechar = null
}) => {
  const { t, i18n } = useTranslation('studentExtra');
  const idBase = useId();
  const id = (sufixo) => `${idBase}-${sufixo}`;
  const botaoRef = useRef(null);
  const campoRef = useRef(null);
  const fimRef = useRef(null);

  const [estado, setEstado] = useState(null);
  const [abertoInterno, setAberto] = useState(false);
  const aberto = controlado ? abertoExterno : abertoInterno;
  const [mensagens, setMensagens] = useState([]);
  const [pergunta, setPergunta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState('');

  const carregarEstado = useCallback(async () => {
    try {
      const r = await api.get('/duvidas-aluno/estado');
      setEstado(r.data || null);
    } catch (_err) {
      // 403 (não é aluno) ou falha: sem botão.
      setEstado(null);
    }
  }, []);

  useEffect(() => { carregarEstado(); }, [carregarEstado]);

  // Prova aberta em outra aba, ou volta para esta aba: confere de novo.
  useEffect(() => {
    const aoMudarStorage = (e) => { if (e.key === CHAVE_PROVA_ABERTA) carregarEstado(); };
    const aoVoltar = () => { if (document.visibilityState === 'visible') carregarEstado(); };
    window.addEventListener('storage', aoMudarStorage);
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      window.removeEventListener('storage', aoMudarStorage);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [carregarEstado]);

  // Outra aula: conversa nova (o contexto da IA é a aula aberta).
  useEffect(() => {
    setMensagens([]);
    setAviso('');
  }, [aulaId]);

  const emProva = Boolean(estado && estado.emProva);
  const visivel = Boolean(estado && estado.visivel) && !emProva && Boolean(turmaId && aulaId);

  const fechar = useCallback(() => {
    if (controlado) {
      if (aoFechar) aoFechar();
      return;
    }
    setAberto(false);
    // Devolve o foco a quem abriu (depois de o painel sumir).
    setTimeout(() => { if (botaoRef.current) botaoRef.current.focus(); }, 0);
  }, [controlado, aoFechar]);

  useEffect(() => {
    if (!visivel && aberto) {
      if (controlado) { if (aoFechar) aoFechar(); } else setAberto(false);
    }
  }, [visivel, aberto, controlado, aoFechar]);

  // Modo controlado: ao abrir pela bolha, confere de novo o estado (prova, limite).
  useEffect(() => {
    if (controlado && abertoExterno) carregarEstado();
  }, [controlado, abertoExterno, carregarEstado]);

  useEffect(() => {
    if (!aberto) return undefined;
    const t0 = setTimeout(() => { if (campoRef.current) campoRef.current.focus(); }, 0);
    const aoTeclar = (e) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault();
        fechar();
      }
    };
    document.addEventListener('keydown', aoTeclar);
    return () => {
      clearTimeout(t0);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto, fechar]);

  useEffect(() => {
    if (fimRef.current && typeof fimRef.current.scrollIntoView === 'function') {
      fimRef.current.scrollIntoView({ block: 'end' });
    }
  }, [mensagens, enviando]);

  if (!visivel) return null;

  const ia = estado.ia || {};
  const restantes = Number.isInteger(ia.restantesHoje) ? ia.restantesHoje : null;
  const perguntaMax = (estado.limites && estado.limites.perguntaMax) || 1000;
  const indisponivel = !ia.disponivel;
  const motivoIndisponivel = indisponivel ? chaveDoErro(ia.motivo === 'bloqueado' ? 403 : 429, { motivo: ia.motivo }) : null;

  const abrir = () => {
    setAberto(true);
    carregarEstado();
  };

  const enviar = async (e) => {
    if (e) e.preventDefault();
    const texto = pergunta.trim();
    if (!texto || enviando || indisponivel) return;
    const historico = mensagens
      .filter((m) => m.papel === 'aluno' || m.papel === 'ia')
      .slice(-HISTORICO_ENVIADO)
      .map((m) => ({ papel: m.papel, texto: m.texto }));
    setMensagens((lista) => [...lista, { papel: 'aluno', texto }]);
    setPergunta('');
    setAviso('');
    setEnviando(true);
    try {
      const r = await api.post('/duvidas-aluno', {
        turmaId, aulaId, pergunta: texto, historico, idioma: (i18n.language || 'pt').slice(0, 2)
      });
      const d = r.data || {};
      setMensagens((lista) => [...lista, { papel: 'ia', texto: d.resposta || '', foraDoEscopo: d.situacao === 'fora_do_escopo' }]);
      setEstado((anterior) => (anterior ? {
        ...anterior,
        ia: {
          ...anterior.ia,
          restantesHoje: Number.isInteger(d.restantesHoje) ? d.restantesHoje : anterior.ia.restantesHoje,
          disponivel: !(Number.isInteger(d.restantesHoje) && d.restantesHoje <= 0),
          motivo: Number.isInteger(d.restantesHoje) && d.restantesHoje <= 0 ? 'limite_usuario' : anterior.ia.motivo
        }
      } : anterior));
    } catch (err) {
      const status = err.response && err.response.status;
      const dados = (err.response && err.response.data) || {};
      const chave = chaveDoErro(status, dados);
      // A pergunta que falhou volta para o campo; a mensagem de erro aparece no painel.
      setMensagens((lista) => lista.slice(0, -1));
      setPergunta(texto);
      if (chave === 'inExam' || chave === 'disabled') {
        await carregarEstado();
        if (chave === 'inExam') setEstado((anterior) => ({ ...(anterior || {}), emProva: dados.prova || { id: null } }));
        return;
      }
      setAviso(t(`aiDoubts.errors.${chave}`));
      if (['limitDay', 'limitMonth', 'limitPlatform', 'blocked'].includes(chave)) carregarEstado();
    } finally {
      setEnviando(false);
    }
  };

  const aoTeclarNoCampo = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      enviar();
    }
  };

  const painel = aberto && createPortal(
    <section
      className="duvida-ia-painel"
      role="dialog"
      aria-modal="false"
      aria-labelledby={id('titulo')}
      aria-describedby={id('intro')}
      aria-busy={enviando}
    >
      <header className="duvida-ia-topo">
        <div>
          <h2 id={id('titulo')}>{t('aiDoubts.title')}</h2>
          {aulaTitulo && <p className="duvida-ia-aula">{t('aiDoubts.lesson', { title: aulaTitulo })}</p>}
        </div>
        <button type="button" className="duvida-ia-fechar" onClick={fechar} aria-label={t('aiDoubts.close')} title={t('aiDoubts.close')}>
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <div className="duvida-ia-conversa" aria-live="polite" aria-relevant="additions">
        <p id={id('intro')} className="duvida-ia-intro">{t('aiDoubts.intro')}</p>
        {mensagens.map((m, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className={`duvida-ia-msg ${m.papel === 'aluno' ? 'aluno' : 'ia'}${m.foraDoEscopo ? ' fora' : ''}`}>
            <span className="duvida-ia-autor">{m.papel === 'aluno' ? t('aiDoubts.you') : t('aiDoubts.tutor')}</span>
            {m.papel === 'aluno' ? <p className="duvida-ia-pergunta">{m.texto}</p> : <TextoMarkdown texto={m.texto} />}
            {m.foraDoEscopo && <small className="duvida-ia-selo">{t('aiDoubts.outOfScope')}</small>}
          </div>
        ))}
        {enviando && (
          <p className="duvida-ia-carregando" role="status">
            <span className="duvida-ia-spinner" aria-hidden="true" />{t('aiDoubts.thinking')}
          </p>
        )}
        <div ref={fimRef} />
      </div>

      {(aviso || indisponivel) && (
        <p className="duvida-ia-aviso" role="alert">
          {aviso || t(`aiDoubts.errors.${motivoIndisponivel}`)}
        </p>
      )}

      <form className="duvida-ia-form" onSubmit={enviar}>
        <label htmlFor={id('campo')} className="duvida-ia-rotulo">{t('aiDoubts.label')}</label>
        <textarea
          id={id('campo')}
          ref={campoRef}
          rows={3}
          maxLength={perguntaMax}
          value={pergunta}
          placeholder={t('aiDoubts.placeholder')}
          onChange={(e) => setPergunta(e.target.value)}
          onKeyDown={aoTeclarNoCampo}
          disabled={indisponivel}
          aria-describedby={id('restantes')}
        />
        <div className="duvida-ia-rodape">
          <small id={id('restantes')} className="duvida-ia-restantes">
            {restantes !== null ? t('aiDoubts.remaining', { count: restantes }) : ''}
          </small>
          <button type="submit" className="duvida-ia-enviar" disabled={enviando || indisponivel || pergunta.trim().length < 2}>
            {enviando ? t('aiDoubts.sending') : t('aiDoubts.send')}
          </button>
        </div>
      </form>
    </section>,
    document.body
  );

  return (
    <>
      {!aberto && !controlado && (
        <button
          type="button"
          ref={botaoRef}
          className="duvida-ia-botao"
          onClick={abrir}
          aria-haspopup="dialog"
        >
          <span aria-hidden="true" className="duvida-ia-botao-icone">?</span>
          <span>{t('aiDoubts.button')}</span>
        </button>
      )}
      {painel}
    </>
  );
};

// Modo 'ia': registra a aula aberta para a bolha "Ajuda" e não desenha nada.
const RegistrarAulaNaAjuda = ({ turmaId, aulaId, aulaTitulo = '' }) => {
  useEffect(() => {
    definirAulaAberta({ turmaId, aulaId, aulaTitulo });
    return () => definirAulaAberta(null);
  }, [turmaId, aulaId, aulaTitulo]);
  return null;
};

const DuvidaIA = ({ modo = MODO_SUPORTE_CHAT, ...props }) => (
  modo === 'ia' ? <RegistrarAulaNaAjuda {...props} /> : <DuvidaIAConversa {...props} />
);

export default DuvidaIA;
